import { Group, Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { SourceTypeFilter } from '@/module/Author/Publication/filters/SourceTypeFilter';
import { JournalTypeFilter } from '@/module/Nomen/JournalType/JournalTypeFilter';

import { AddButtonLink, Browse, NumberFilter, useFilter, useNumberFilter, usePagination } from '@/core/components';

import { FilterBox, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { ReviewCard } from './components/Card/ReviewCard';
import { getReviewPagedOptions } from './model/ReviewQuery';

export function ReviewBrowse() {
  const { t } = useTranslation();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, ...paginationProps } = usePagination({ search });

  const { id: journalTypeId, ...typeFilterProps } = useFilter();
  const { id: sourceType, ...sourceTypeFilterProps } = useFilter();
  const { item: publishYear, ...publishYearFilterProps } = useNumberFilter();
  const { item: indexingYear, ...indexingYearFilterProps } = useNumberFilter();

  const { data } = useSuspenseQuery(
    getReviewPagedOptions({
      ...pagination,
      sortDirection,
      search,
      sourceType,
      journalTypeId,
      publishYear,
      indexingYear,
    }),
  );
  useTotalElements(data);

  return (
    <Browse
      actions={<AddButtonLink to="search-claimable" />}
      title={t('reviews.title')}
      subtitle={t('reviews.subtitle')}
      pagination={paginationProps}
      filters={
        <FilterBox>
          <Stack>
            <SearchFilter
              label={t('label.search')}
              description={t('articles.searchBy')}
              {...searchProps}
            />
            <Group>
              <NumberFilter
                label={t('articles.filters.publishYear')}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
                {...publishYearFilterProps}
                flex={0.25}
              />
              <NumberFilter
                label={t('articles.filters.indexingYear')}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
                {...indexingYearFilterProps}
                flex={0.25}
              />
              <JournalTypeFilter
                label={t('articles.filters.journalType')}
                {...typeFilterProps}
                flex={0.25}
              />
              <SourceTypeFilter
                label={t('publications.filters.sourceType')}
                {...sourceTypeFilterProps}
                flex={0.25}
              />
            </Group>
          </Stack>
        </FilterBox>
      }
      sortDirection={sortDirection}
      toggleSortDirection={toggleSortDirection}
      sortedBy={t('projects.sortedByPublishDate')}
      data={data.list}
      renderItem={review => (
        <ReviewCard
          key={review.id}
          review={review}
          to={`${review.id}`}
        />
      )}
    />
  );
}
